import { Inject } from '@odinjs/odin';
import { ActionAPI, ButtonAPI, DeckAPI, DeckValue, ModalButton, ModalProps, ModalService, PanelAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { ModalButtonType, Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/message-service';

import { applicationContext } from '@philips-internal/oswald-ui-foundation';

import { Endpoint } from '../../../constants/endpoints';
import { ActionType, AdminType, InfusionHourStatus, InfusionItemStatus, InfusionScreenType, OptionCheck } from '../../../constants/infusion-solutions';
import { InfusionDetail } from '../../../interfaces/infusion';
import { FoundationUtils } from '../../../utils/foundation-utils';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../utils/parameters';
import BaseController from '../../BaseController';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { MedicationAndDateHeaderParams } from '../../commons/header/medication-and-date-header-params';
import { ScheduleMedicationParams } from '../../schedule-medication-infusion/schedule-medication-infusion-params';
import { AdminByProcessParams } from '../eletronic-prescription/medication-administration-by-process/medication-administration-by-process-params';
import { MedicationViewerParams } from '../eletronic-prescription/medication-viewer/medication-viewer-params';
import { MedicineAndSolutionsParams } from '../eletronic-prescription/medicine-and-solutions/medicine-and-solutions-params';
import { AdminModalsService } from '../services/admin-modals-service';
import InfusionService from '../services/infusion-service';
import MedicationService from '../services/medication-service';
import TimelineContext from '../services/timeline-service';
import { ApplicationRoute } from '../../../constants/routes';
import { InfusionsViewerParams } from './infusions-viewer-params';

import './infusion-viewer-controller.css';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { FragmentNames, InfusionsViewerAliases } from '../../../codegen';
import { YesNoType } from '../../../utils/enum-utils';
import { Params } from '../eletronic-prescription/administration-schedule/administration-schedule-types';

interface InfusionViewerParams {
  allowOnlyConsultingEmar: boolean,
  automaticallyCalculateInfusedVolumeOfSolutions: boolean,
  onlyEnableEndSolutionOptionInTheLastStep: boolean,
  requireJustification: boolean
}

interface ResponseShow {
  status: string;
}

interface ProcessSequence {
  processSequence?: string;
}

@Controller({ component: InfusionsViewerAliases.LAYOUT_INFUSIONS_VIEWER, fragment: FragmentNames.INFUSIONS_VIEWER })
class InfusionViewerController extends BaseController {

  @Inject(InfusionsViewerAliases.INFUSION_STOP_BUTTON)
    infusionStopButtonApi!: ButtonAPI;

  @Inject(InfusionsViewerAliases.CONTEXTUAL_BAR_ACTION_SUSPEND)
    actionSuspend!: ActionAPI;

  @Inject(InfusionsViewerAliases.CONTEXTUAL_BAR_ACTION_SCHEDULE)
    actionScheduleApi!: ActionAPI;

  @Inject(InfusionsViewerAliases.CONTEXTUAL_BAR_ACTION_ADJUST_SPEED)
    actionAdjustSpeed!: ActionAPI;

  @Inject(InfusionsViewerAliases.CONTEXTUAL_BAR_ACTION_TERMINATE)
    actionTerminate!: ActionAPI;

  @Inject(InfusionsViewerAliases.INFUSION_REVERSE_LAST_EVENT_ACTION)
    actionReverseLastEvent!: ActionAPI;

  @Inject(InfusionsViewerAliases.ADMINISTRATION_BY_PROCESS_ACTION)
    administrationByProcessActionApi!: ActionAPI;

  @Inject(InfusionsViewerAliases.ADMINISTRATION_BY_SCHEDULE_ACTION)
    administrationByScheduleActionApi!: ActionAPI;

  @Inject(InfusionsViewerAliases.INFUSIONS_VIEWER_DECK)
    infusionsViewerDeckApi!: DeckAPI;

  @Inject(InfusionsViewerAliases.INFUSIONS_VIEWER_BUTTON_PANEL_CONTAINER)
    infusionsViewerbuttonPanelContainer!: PanelAPI;

  state = {
    param27: FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_ONLY_CONSULTING_EMAR)
  }

  redirectAdjustInfusionSpeedScreen(): void {
    const { patientId, encounterId, prescriptionNumber, sequenceNumber } = this.historyService.getCurrentRouteParams();

    InfusionsViewerParams.params = {
      currentSpeedInfusion: MedicineAndSolutionsParams.speedParam.currentSpeedInfusion,
      establishmentCode: MedicineAndSolutionsParams.params.nrGroupingId,
      infusionName: MedicineAndSolutionsParams.params.dsMaterialId,
      prescriptionNumber: String(prescriptionNumber),
      solutionNumber: String(sequenceNumber),
    }

    applicationContext.redirect(ApplicationRoute.ADEP_ADJUST_INFUSION_SPEED, {
      encounterId,
      patientId,
    });
  }

  updateOptionMenu(): void {
    const requireJustification = this.context.get('requireJustification') as boolean;
    const currentRoute = this.historyService.getCurrentRoute();

    if (!requireJustification) {
      applicationContext.redirect(`${currentRoute}/suspend`);
    }

    applicationContext.redirect(`${currentRoute}/suspend/justification`);
  }

  validateOptionAdjustSpeedIsVisible(): void {
    const paramTwsOrTasy = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_ONLY_CONSULTING_EMAR);
    const { hourStatus: status, itemStatus } = this.historyService.getCurrentRouteParams();
    const validItemStatus = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED].some(st => st === itemStatus);
    const allowAdjustSpeed = paramTwsOrTasy === YesNoType.NO && status === InfusionHourStatus.STARTED && validItemStatus;

    if(allowAdjustSpeed) {
      this.actionAdjustSpeed.setEnabled(true);
    }
  }

  validateOptionSuspendIsVisible(): void {
    const { prescriptionNumber, sequenceNumber } = this.historyService.getCurrentRouteParams();

    HttpService.get(Endpoint.LIST_PRESCRIPTION_STEPS, {
      prescriptionNumber,
      sequenceNumber
    }).then(response => {
      const list = response.body as Array<Value>;

      this.actionSuspend.setEnabled(list.length > 0);
    }).catch(() => {
      this.actionSuspend.setEnabled(false);
    })
  }

  showFunctionAdministrationByProcessOrBySchedule(adminType: string, status: string): boolean {
    const param8 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.HOW_TO_ADMINISTER_THE_MEDICATION);
    const adminTypeDescription = 'schedule';
    const processTypeDescription = 'process';
    const processOrSchedule = param8 === AdminType.PROCESS_OR_SCHEDULE;
    const preparation = status === InfusionHourStatus.PREPARATION;
    const auxAllowByProcessOrBySchedule = processOrSchedule && preparation;

    const allowByProcessOrBySchedule = ((adminType === adminTypeDescription || adminType === processTypeDescription) && auxAllowByProcessOrBySchedule);

    if (allowByProcessOrBySchedule) {
      return true;
    }

    return false;
  }

  openAdminByScheduleModal(status: string): void {
    const processNumber = this.context.get('processSequence');
    const admBySchedule = this.context.get('admBySchedule');
    const admByProcess = this.context.get('admByProcess');

    if (admBySchedule) {
      const adminType = 'infusion-admin-schedule';

      AdminModalsService.modalAdminByProcessOrScheduleIsVisible(adminType, {});
      AdminByProcessParams.admType = { isAdmProcess: false };
      return;
    }

    if (admByProcess) {
      const adminType             = 'infusion-admin-process';
      const medicationOrSolution  = 'solution';
      const infusionName          = InfusionsViewerParams.suspendParams?.infusionName;
      const errorMessage          = MessageService.translate('text.the-medication-is-not-prepared');
      const errorMessageFormatted = errorMessage.replace('#@medicamento#@', String(infusionName));
      const allowScheduleModal    = status === InfusionHourStatus.PREPARATION;

      if (allowScheduleModal) {
        AdminModalsService.modalAdminByProcessOrScheduleIsVisible(adminType, {
          medicationOrSolution,
          processSequence: Number(processNumber),
        });
        AdminByProcessParams.admType = { isAdmProcess: true };
      } else {
        MedicationService.showToast(errorMessageFormatted, ToastLevel.ERROR);
        return;
      }
    }
  }

  async setScreenLabel(actionType: string, status: string): Promise<void> {
    const param3 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CHECK_MEDICATIONS_BEFORE_ADMIN);
    const param8 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.HOW_TO_ADMINISTER_THE_MEDICATION);
    const param620 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOWS_YOU_TO_PERFORM_THE_FIRST_INFUSION_CHECK);
    const { itemStatus } = this.historyService.getCurrentRouteParams();
    const solutionItemStatusInterrupted = (itemStatus === InfusionItemStatus.II || itemStatus === InfusionItemStatus.INTERRUPTED);
    const allowInfusionRestart          = ((status === InfusionHourStatus.INTERRUPTED && solutionItemStatusInterrupted) ||
                                          ((status === InfusionHourStatus.PENDING || status === InfusionHourStatus.PREPARATION) && (itemStatus === InfusionItemStatus.INTERRUPTED)));
    const allowStartSolutionByProcess   = ((status === InfusionHourStatus.PREPARATION) && (param8 ===  AdminType.PROCESS || param8 === AdminType.PROCESS_OR_SCHEDULE)) || (param8 ===  AdminType.PROCESS);
    const allowStartSolutionBySchedule  = (status !== InfusionHourStatus.STARTED) && (param8 === AdminType.SCHEDULE || param8 === AdminType.PROCESS_OR_SCHEDULE);
    const isFirstCheck = await InfusionService.validateStatusMedication(OptionCheck.FIRST_CHECK);
    const adminTypeValid = param8 === AdminType.SCHEDULE || param8 === AdminType.PROCESS_OR_SCHEDULE;
    const firstCheck = param620 === Params.ALLOWED && isFirstCheck && (adminTypeValid || param3 === Params.NOT_ALLOWED);

    InfusionsViewerParams.redirectToInfusionSchedule = true;
    InfusionsViewerParams.realizeFirstChecking = firstCheck;

    const buttonLabel = actionType !== ActionType.START_SOLUTION && 'button.infusion-viewer.restart';
    const infusionScreen = actionType === ActionType.START_SOLUTION ? InfusionScreenType.START_SOLUTION : InfusionScreenType.RESTART_SOLUTION;

    this.infusionStopButtonApi.setValue(MessageService.translate(String(buttonLabel)));
    InfusionsViewerParams.infusionScreenType = infusionScreen;
    InfusionsViewerParams.actionType = actionType;

    if (firstCheck) {
      this.infusionStopButtonApi.setValue(MessageService.translate('tws.emar.perform-first-check'));
      InfusionsViewerParams.infusionScreenType = InfusionScreenType.FIRST_CHECK;
      this.context.set({
        admBySchedule: true
      })
      return;
    }

    if (allowInfusionRestart) {
      this.infusionStopButtonApi.setValue(MessageService.translate('button.infusion-viewer.restart'));
      InfusionsViewerParams.infusionScreenType = InfusionScreenType.RESTART_SOLUTION;
      this.context.set({
        admBySchedule: true
      })
      return;
    }

    if (allowStartSolutionByProcess) {
      this.infusionStopButtonApi.setValue(MessageService.translate('text.start-solution-by-process'));
      this.context.set({
        admByProcess: true
      })
      return;
    }

    if (allowStartSolutionBySchedule) {
      this.infusionStopButtonApi.setValue(MessageService.translate('text.start-solution-by-schedule'));
      this.context.set({
        admBySchedule: true
      })
      return;
    }

    this.infusionStopButtonApi.setVisible(false);
  }

  validateIsExistProcessNumberAndShowActionProcess(): void {
    if (this.state.param27 !== YesNoType.YES) {
      const adminType = 'process';
      const { hourStatus, scheduleSequenceNumber, status } = this.historyService.getCurrentRouteParams();
      const resolvedStatus = status ?? hourStatus;
      const processActionIsVisible = this.showFunctionAdministrationByProcessOrBySchedule(adminType, String(resolvedStatus));

      HttpService.get(Endpoint.EMAR_GET_NUMBER_PROCESS, { tagNumber: Number(scheduleSequenceNumber) })
        .then(res => {
          const response = res.body as Record<string, Value>[] as ProcessSequence;
          const processSequence = response?.processSequence;

          this.context.set({ processSequence });

          if (processActionIsVisible) {
            this.administrationByProcessActionApi.setEnabled(true);
            return;
          }
        })
    }
  }

  @OnMount({ target: InfusionsViewerAliases.LAYOUT_INFUSIONS_VIEWER })
  async handleMountLayout(): Promise<void> {
    this.findInfusionInformations();
  }

  private findInfusionInformations(): void {
    const {
      patientId, encounterId, prescriptionNumber, sequenceNumber, sequenceNumber: solutionId, itemStatus, hourStatus
    } = this.historyService.getCurrentRouteParams();
    const param5 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.REQUIRE_JUSTIFICATION_ON_SUSPEND_ITEM);
    const isDelayed = TimelineContext.get().delayed;
    this.infusionsViewerDeckApi.setDone(true);
    this.infusionsViewerDeckApi.setLoading(true);
    const cpoeMaterial = TimelineContext.getCpoeMaterial();

    this.findInfusionsDetailInformation().then(res => {
      const responseBody = res as Record<string, Value>[];
      const infusionType = 'INFUSION';

      responseBody[0].description = this.validateItemDescription(responseBody[0].description as string);

      const deckValue = {
        cpoeMaterial: cpoeMaterial,
        ...responseBody[0],
        hourStatus: hourStatus,
        isDelayed,
        isSolution: true,
      } as DeckValue;

      InfusionsViewerParams.suspendParams = {
        infusionDateTime: String(deckValue?.statusDate),
        infusionName: String(deckValue?.description),
      };

      InfusionsViewerParams.adminSchedule = {
        dilutionDescription: String(deckValue?.dilutionDescription),
        dosageTypeDescription: String(deckValue?.dosageTypeDescription),
        observationDescription: String(deckValue?.observationDescription),
        prescriptionNumber: Number(deckValue?.prescriptionId),
        solutionSequenceNumber: Number(deckValue?.solutionId),
        stepSolutionNumber: Number(deckValue?.stepSolutionNumber)
      };

      MedicationAndDateHeaderParams.medicationAndDateHeaderProps = {
        medicationName: String(deckValue?.description),
        scheduleDate: String(deckValue?.scheduleDate),
        type: infusionType
      };

      this.infusionsViewerDeckApi.setData([deckValue]);
    }).catch((err) => {
      if (err.body) {
        const message = err.body.message;
        this.showToastErrorShowOption(message);
      }
    }).finally(() => this.infusionsViewerDeckApi.setLoading(false));

    InformationBarUtils.collapsedBar = true;

    this.context.set({
      encounterId,
      hourStatus,
      itemStatus,
      patientId,
      prescriptionNumber,
      requireJustification: YesNoType.YES === param5,
      sequenceNumber,
      solutionId,
    });
  }

  private validateItemDescription(description : string) : string {
    const solutionDescription = MedicineAndSolutionsParams?.params?.dsMaterialId;
    if (solutionDescription
      && solutionDescription.indexOf('+') > -1) {
      return solutionDescription;
    }
    return description;
  }

  @OnMount({ target: InfusionsViewerAliases.CONTEXTUAL_BAR_ACTION_SUSPEND })
  handleMountContextualBarActionSuspend(): void {
    const { hourStatus: status, itemStatus } = this.historyService.getCurrentRouteParams();
    const oneValidateStatus = status === InfusionHourStatus.ADMINISTERED && itemStatus === InfusionItemStatus.T;
    const twoValidateStatus = status === InfusionHourStatus.PENDING && itemStatus === InfusionItemStatus.T;

    this.initializeParams();

    if (this.state.param27 !== YesNoType.YES) {
      if (oneValidateStatus || twoValidateStatus) {
        this.actionSuspend.setEnabled(false);
      } else {
        this.validateOptionSuspendIsVisible();
      }
    }
  }

  @OnMount({ target: InfusionsViewerAliases.CONTEXTUAL_BAR_ACTION_SCHEDULE })
  handleMountContextualBarActionReschedule(): void {
    this.validateOptionScheduleIsVisible();
  }

  @OnMount({ target: InfusionsViewerAliases.CONTEXTUAL_BAR_ACTION_TERMINATE })
  handleMountContextualBarActionTerminate(): void {
    const { hourStatus: status, itemStatus } = this.historyService.getCurrentRouteParams();
    const oneValidItemStatus = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED].some(st => st === itemStatus);
    const twoValidItemStatus = [InfusionItemStatus.INTERRUPTED, InfusionItemStatus.II].some(st => st === itemStatus);
    const allowTerminate     = (status === InfusionHourStatus.STARTED && oneValidItemStatus) || (status === InfusionHourStatus.INTERRUPTED && twoValidItemStatus);

    if (this.state.param27 !== YesNoType.YES) {
      if (allowTerminate) {
        this.actionTerminate.setEnabled(true);
        return;
      }

      if(this.shouldShowTerminateAction()) {
        this.actionTerminate.setEnabled(true);
      }
    }
  }

  @OnMount({ target: InfusionsViewerAliases.ADMINISTRATION_BY_PROCESS_ACTION })
  handleMountProcessAction(): void {
    const { hourStatus: status, itemStatus } = this.historyService.getCurrentRouteParams();
    const showActionAdmProcess = (status === InfusionHourStatus.PENDING || status === InfusionHourStatus.PREPARATION) && (itemStatus === InfusionItemStatus.NOT_STARTED);

    if (showActionAdmProcess) {
      this.validateIsExistProcessNumberAndShowActionProcess();
    }
  }

  @OnMount({ target: InfusionsViewerAliases.ADMINISTRATION_BY_SCHEDULE_ACTION })
  handleMountScheduleAction(): void {
    const { hourStatus: status, itemStatus } = this.historyService.getCurrentRouteParams();
    const showActionsAdmSchedule = (status === InfusionHourStatus.PENDING || status === InfusionHourStatus.PREPARATION) && (itemStatus === InfusionItemStatus.NOT_STARTED);

    if (this.state.param27 !== YesNoType.YES && showActionsAdmSchedule) {
      const adminType = 'schedule';
      const scheduleActionIsVisible = this.showFunctionAdministrationByProcessOrBySchedule(adminType, String(status));

      if (scheduleActionIsVisible) {
        this.administrationByScheduleActionApi.setEnabled(true);
      }
    }
  }

  @OnClick({ target: InfusionsViewerAliases.ADMINISTRATION_BY_SCHEDULE_ACTION })
  handleClickScheduleAction(): void {
    InfusionsViewerParams.infusionScreenType = InfusionScreenType.ADMIN_BY_SCHEDULE;
    const adminType = 'infusion-admin-schedule';

    AdminModalsService.modalAdminByProcessOrScheduleIsVisible(adminType, {});
    AdminByProcessParams.admType = { isAdmProcess: false };
  }

  @OnClick({ target: InfusionsViewerAliases.ADMINISTRATION_BY_PROCESS_ACTION })
  handleClickProcessAction(): void {
    const processNumber        = this.context.get('processSequence');
    const adminType            = 'infusion-admin-process';
    const medicationOrSolution = 'solution';

    AdminModalsService.modalAdminByProcessOrScheduleIsVisible(adminType, {
      medicationOrSolution,
      processSequence: Number(processNumber),
    });
  }

  validateOptionScheduleIsVisible(): void {
    const param118          = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_SCHEDULE_SOLUTION_STEP);
    const { encounterId, hourStatus: status, itemStatus } = this.historyService.getCurrentRouteParams();
    const validItemStatus   = [InfusionItemStatus.STARTED, InfusionItemStatus.II, InfusionItemStatus.INTERRUPTED, InfusionItemStatus.NOT_STARTED, InfusionItemStatus.RESTARTED].some(st => st === itemStatus);
    const validStatusIsTrue = validItemStatus && [InfusionHourStatus.PENDING, InfusionHourStatus.PREPARATION].some(st => st === status);
    const data              = MedicineAndSolutionsParams?.params?.startId ? MedicineAndSolutionsParams.params.startId : '';
    const itemType          = 'M';

    const body = {
      itemNumber: MedicineAndSolutionsParams.params.nrSequenceId,
      itemType,
      prescriptionNumber: encounterId,
      time: new Date(data).toISOString(),
      timeNumber: MedicineAndSolutionsParams.params.hourSequence,
    } as Record<string, Value>;

    if (param118 === YesNoType.YES && validStatusIsTrue) {
      HttpService.post(Endpoint.SHOW_SCHEDULE_OPTION, {},
        { body }
      ).then(response => {
        const resp = response.body as ResponseShow;
        if (resp.status !== YesNoType.YES) {
          this.actionScheduleApi.setEnabled(true);
        }
      }).catch((err) => {
        if (err.body.message !== '') {
          const message = err.body.message;
          this.showToastErrorShowOption(message);
        }
      });
    }
  }

  @OnClick({ target: InfusionsViewerAliases.CONTEXTUAL_BAR_ACTION_SUSPEND })
  handleClickSuspendAction(): void {
    const currentRoute = this.historyService.getCurrentRoute();

    const params = this.context.get('params') as InfusionViewerParams;

    if(!params.requireJustification) {
      applicationContext.redirect(`${currentRoute}/suspend`);
      return;
    }

    applicationContext.redirect(`${currentRoute}/suspend/justification`);
  }

  @OnClick({ target: InfusionsViewerAliases.CONTEXTUAL_BAR_ACTION_TERMINATE })
  handleClickTerminateAction(): void {
    this.goToTerminateSolution();
  }

  @OnClick({ target: InfusionsViewerAliases.INFUSION_REVERSE_LAST_EVENT_ACTION })
  handleClickContextualBarOpenRevertLastEvent(): void {
    this.openRevertLastEvent();
  }

  openRevertLastEvent(): void {
    const { patientId, encounterId, prescriptionNumber, sequenceNumber } = this.historyService.getCurrentRouteParams();
    const REVERSE_EVENT = '/application/electronic-medication-administration/personal-information/patients/:patientId/encounters/:encounterId/infusion-viewer/:prescriptionNumber/:sequenceNumber/reverse-last-event';

    applicationContext.redirect(
      REVERSE_EVENT,
      {
        encounterId,
        patientId,
        prescriptionNumber,
        sequenceNumber,
      }
    );
  }

  @OnMount({ target: InfusionsViewerAliases.CONTEXTUAL_BAR_ACTION_ADJUST_SPEED })
  handleMountContextualBarActionAdjustSpeed(): void {
    if (this.state.param27 !== YesNoType.YES) {
      this.validateOptionAdjustSpeedIsVisible();
    }
  }

  @OnClick({ target: InfusionsViewerAliases.CONTEXTUAL_BAR_ACTION_ADJUST_SPEED })
  handleClickActionAdjustSpeed(): void {
    this.redirectAdjustInfusionSpeedScreen();
  }

  private addNewParams(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const data                       = MedicineAndSolutionsParams?.params?.startId ? MedicineAndSolutionsParams.params.startId : '';
    const itemType                   = 'SOL';

    MedicationViewerParams.params = {
      eventDate: data,
      hourSequence: MedicineAndSolutionsParams.params.hourSequence,
      itemCode: MedicineAndSolutionsParams.params.cdMaterialId,
      medicationName: MedicineAndSolutionsParams.params.dsMaterialId,
      prescriptionNumber: MedicineAndSolutionsParams.params.nrPrescriptionId,
    }

    MedicineAndSolutionsParams.params.ieTypeItem = itemType;

    ScheduleMedicationParams.prototype.encounter = encounterId as number;

    const route = ApplicationRoute.EMAR_SCHEDULE_MEDICATION_INFUSION;

    applicationContext.redirect(route, {
      encounterId,
      patientId,
    });
  }

  @OnClick({ target: InfusionsViewerAliases.CONTEXTUAL_BAR_ACTION_SCHEDULE })
  handleClickActionSchedule(): void {
    const paramTasyOrTws146 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.SCHEDULE_SOLUTION_WITHOUT_LIBERATION);
    const { prescriptionNumber } = this.historyService.getCurrentRouteParams();
    HttpService.get(
      Endpoint.VERIFY_CAN_SCHEDULE_SOLUTION, {prescriptionNumber}
    ).then(response => {
      const resp = response.body;

      const hasPrescription = MedicineAndSolutionsParams?.params.nrPrescriptionsId != null || MedicineAndSolutionsParams?.params.nrPrescriptionId != null;

      if ((hasPrescription && paramTasyOrTws146 === YesNoType.YES) || resp != null && resp !== '') {
        this.addNewParams();
      } else {
        this.showToastErrorInfusion();
      }
    }).catch((err) => {
      if (err.body != undefined) {
        this.showToastErrorInfusion();
      }
    });

  }

  showToastErrorInfusion(): void {
    const message = MessageService.translate('tws.schedule-infusion-error').replace('#@solucao#@', `${MedicineAndSolutionsParams.params.dsMaterialId}`);

    return ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'schedule-time-item-prescription',
      level: ToastLevel.WARNING,
      message: message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  shouldShowInterrupt(itemStatus: string, status: string, isSetVisible: boolean): boolean {
    const isItemStatusValid = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED].some(st => st === itemStatus);
    const isStatusValid = status === InfusionHourStatus.STARTED;

    if (isItemStatusValid && isStatusValid) {
      if (isSetVisible) {
        this.infusionsViewerbuttonPanelContainer.setVisible(true);
      } else {
        this.infusionStopButtonApi.setValue(MessageService.translate('button.infusion-viewer.interrupt'));
      }
      return true;
    }
    return false;
  }

  shouldShowStartSolution(itemStatus: string, status: string, isSetVisible: boolean): boolean {
    const param13                       = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.ALLOWS_TO_PERFORM_PRESCRIPTION_ITEMS);
    const dependsOnTheRule              = 'D';
    const isParam13Valid                = param13 === YesNoType.YES || param13 === dependsOnTheRule;
    const isItemStatusValid             = itemStatus === InfusionItemStatus.NOT_STARTED;
    const isStatusValid                 = status === InfusionHourStatus.PENDING || status === InfusionHourStatus.PREPARATION || status === InfusionHourStatus.CHECKED_FIRST;
    const { emarStepScheduleNumber, isPdo, stepSolutionNumber } = this.context.getBulkData() as { emarStepScheduleNumber: number, isPdo: string, stepSolutionNumber: number };
    const isStatusSolutionValid         = isPdo === YesNoType.NO || stepSolutionNumber === 1 || emarStepScheduleNumber === 1;

    if (isParam13Valid && isItemStatusValid && isStatusValid && isStatusSolutionValid) {
      if (isSetVisible) {
        this.infusionsViewerbuttonPanelContainer.setVisible(true);
      } else {
        this.setScreenLabel(ActionType.START_SOLUTION, status);
      }
      return true;
    }
    return false;
  }

  shouldShowRestartSolution(itemStatus: string, status: string, isSetVisible: boolean): boolean {
    const solutionItemStatusInterrupted = ((itemStatus === InfusionItemStatus.II || itemStatus === InfusionItemStatus.INTERRUPTED) && (status === InfusionHourStatus.INTERRUPTED));
    const solutionItemStatusPending = (itemStatus === InfusionItemStatus.INTERRUPTED && (status === InfusionHourStatus.PENDING || status === InfusionHourStatus.PREPARATION));

    if (solutionItemStatusInterrupted || solutionItemStatusPending) {
      if (isSetVisible) {
        this.infusionsViewerbuttonPanelContainer.setVisible(true);
      } else {
        this.setScreenLabel(ActionType.RESTART_SOLUTION, status);
      }
      return true;
    }
    return false;
  }

  setInfusionDetailProcess(data: InfusionDetail): void {
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();
    const measureLabel = MessageService.translate('tws.measure-label');
    data.infusedVolumeQty = `${data.infusedVolumeQty} ${measureLabel}`;
    data.despisedVolumeQty = `${data.despisedVolumeQty} ${measureLabel}`;

    ScheduleMedicationParams.prototype.solutionNumber = data.stepSolutionNumber;
    ScheduleMedicationParams.prototype.step = data.stepQuantity;
    ScheduleMedicationParams.prototype.scheduleSequenceNumber = Number(scheduleSequenceNumber);

    this.context.set({
      balanceStepNumber: data.balanceStepNumber,
      emarStepScheduleNumber: data.stepSolutionNumber,
      ieStatus: data.status,
      isPdo: data.isPdo,
      stepQuantity: data.stepQuantity,
    });

    this.validateIfButtonIsVisible(true);
  }

  initializeParams(): void {
    const requireJustification = this.getBoolParam(ADEP_E1113Parameters.REQUIRE_JUSTIFICATION_ON_SUSPEND_ITEM);
    const allowOnlyConsultingEmar = this.getBoolParam(ADEP_E1113Parameters.ALLOW_ONLY_CONSULTING_EMAR);
    const onlyEnableEndSolutionOptionInTheLastStep = this.getBoolParam(ADEP_E1113Parameters.ONLY_ENABLE_END_SOLUTION_OPTION_IN_THE_LAST_STEP);
    const automaticallyCalculateInfusedVolumeOfSolutions = this.getBoolParam(ADEP_E1113Parameters.AUTOMATICALLY_CALCULATE_INFUSED_VOLUME_SOLUTIONS);

    const params = {
      allowOnlyConsultingEmar,
      automaticallyCalculateInfusedVolumeOfSolutions,
      onlyEnableEndSolutionOptionInTheLastStep,
      requireJustification,
    } as InfusionViewerParams;

    this.context.set({params});
  }

  getBoolParam(param: string): boolean {
    return YesNoType.YES === FoundationUtils.findModuleParam(FunctionName.ADEP_1113, param);
  }

  shouldShowTerminateAction(): boolean {
    const params = this.context.get('params') as InfusionViewerParams;

    if(params.allowOnlyConsultingEmar){
      return false
    }

    const itemStatus = this.context.get('itemStatus') as string;
    const stepNumber = this.context.get('stepQuantity') as number;
    const hourStatus = this.context.get('hourStatus') as string;
    const stepSolutionNumber = this.context.get('stepSolutionNumber') as number;
    const balanceStepNumber = this.context.get('balanceStepNumber') as number;
    const isPdo = this.context.get('isPdo') as string;

    if(stepNumber === stepSolutionNumber) {
      return false;
    }

    const invalidHourStatus  = [InfusionHourStatus.CHECKED, InfusionHourStatus.PREPARATION, InfusionHourStatus.PENDING].some(st=>st===hourStatus);

    if(invalidHourStatus) {
      return false;
    }

    if(params.onlyEnableEndSolutionOptionInTheLastStep){
      const validIeStatus = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED, InfusionItemStatus.II].some(st=>st===itemStatus);

      if(!validIeStatus) {
        return false;
      }
      const validStepNumber = stepNumber !== null;
      const validBalanceStepNumber = balanceStepNumber === 0;
      const isPdoSAndIsLastStep = isPdo === YesNoType.YES && stepNumber === stepSolutionNumber;

      return validStepNumber || validBalanceStepNumber || isPdoSAndIsLastStep;
    }

    return [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED, InfusionItemStatus.INTERRUPTED, InfusionItemStatus.II].some(st=>st===itemStatus);
  }

  goToTerminateSolution(): void {
    const { patientId, encounterId, hourStatus, prescriptionNumber, sequenceNumber, startDate, itemStatus, currentSpeedInfusion } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.ADEP_INFUSION_TERMINATE, {currentSpeedInfusion, encounterId, hourStatus, itemStatus, patientId, prescriptionNumber, sequenceNumber, startDate});
  }

  private redirectRegisterPartialVolume(): void {
    const { encounterId, hourStatus, patientId, prescriptionNumber, sequenceNumber, startDate, itemStatus, currentSpeedInfusion } = this.historyService.getCurrentRouteParams();

    applicationContext.redirect(
      ApplicationRoute.EMAR_REGISTER_PARTIAL_VOLUME_INFUSION,
      {
        currentSpeedInfusion,
        encounterId,
        hourStatus,
        itemStatus,
        patientId,
        prescriptionNumber,
        sequenceNumber,
        startDate,
      }
    );
  }

  private redirectInterruptSolution(): void {
    const route = ApplicationRoute.EMAR_INTERRUPT_SOLUTION;

    const {
      patientId,
      encounterId,
      prescriptionNumber,
      sequenceNumber,
      startDate,
      hourStatus,
      itemStatus,
      currentSpeedInfusion
    } = this.historyService.getCurrentRouteParams();

    this.context.set({inInfusionInterruptRouteStack: true});

    applicationContext.redirect(
      route,
      {
        currentSpeedInfusion,
        encounterId,
        hourStatus,
        itemStatus,
        patientId,
        prescriptionNumber: prescriptionNumber,
        sequenceNumber,
        startDate,
      }
    );
  }

  private displayConfirmationDialog(): void {
    const buttons: ModalButton[] = [];

    buttons.push({
      action: () => { this.redirectInterruptSolution() },
      title: 'label.status-no',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => { this.redirectRegisterPartialVolume() },
      title: 'tws.emar.button-yes',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: 'tws.emar.confirm-partial-volume-record-when-stopping-infusion',
      dockButtons: buttons,
      focusOnOpen: true,
      onCancel: () => { },
      onSubmit: () => { },
      title: '',
      uuid: generateUUID(),
    };

    ModalService.openDialog(dialogModal);
  }

  validateIfButtonIsVisible(isSetVisible: boolean): void {
    const param27                            = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_ONLY_CONSULTING_EMAR);
    const { hourStatus: status, itemStatus } = this.historyService.getCurrentRouteParams();
    const isItemStatusValid                  = [InfusionItemStatus.II, InfusionItemStatus.INTERRUPTED, InfusionItemStatus.RESTARTED, InfusionItemStatus.T].some(st => st === itemStatus);
    const allowInfusionButton                = (status === InfusionHourStatus.ADMINISTERED && isItemStatusValid) || (status === InfusionHourStatus.PENDING && itemStatus === InfusionItemStatus.RESTARTED);

    if (param27 !== YesNoType.YES) {
      if (allowInfusionButton) {
        this.infusionsViewerbuttonPanelContainer.setVisible(false);
        return;
      }

      if (this.shouldShowStartSolution(String(itemStatus), String(status), isSetVisible)) {
        return;
      }

      if (this.shouldShowRestartSolution(String(itemStatus), String(status), isSetVisible)) {
        return;
      }

      if (this.shouldShowTerminateAction()) {
        this.actionTerminate.setEnabled(true);
      }

      if (this.shouldShowInterrupt(String(itemStatus), String(status), isSetVisible)) {
        return;
      }
    }
  }

  @OnMount({ target: InfusionsViewerAliases.INFUSION_STOP_BUTTON})
  handleMountInfusionStopButton(): void {
    this.validateIfButtonIsVisible(false);
  }

  @OnClick({ target: InfusionsViewerAliases.INFUSION_STOP_BUTTON })
  handleClickInfusionStopButton(): void {
    const { hourStatus: status } = this.historyService.getCurrentRouteParams();

    if (InfusionsViewerParams.redirectToInfusionSchedule) {
      this.openAdminByScheduleModal(String(status));
      return;
    }

    const param75 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ASK_USER_IF_THEY_WANT_TO_LOG_PARTIAL_VOLUMES_WHEN_STOPPING_SOLUTIONS);

    if (param75 === YesNoType.YES) {
      this.displayConfirmationDialog();
      return;
    }

    this.redirectInterruptSolution();
  }

  showToastErrorShowOption(value: string ): void {
    return ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'show-option-error',
      level: ToastLevel.ERROR,
      message: value,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  findInfusionsDetailInformation(): Promise<void | Record<string, Value>[] | null> {
    const param27 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_ONLY_CONSULTING_EMAR);
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();

    return HttpService.get(Endpoint.GET_INFUSIONS_ONGOING_DETAIL, { scheduleSequenceNumber })
      .then((res) => {
        const infusionDetail = res.body as InfusionDetail;
        InfusionsViewerParams.infusionDetailsParams = infusionDetail;
        const responseBody = res.body as Record<string, Value>;

        if (param27 !== YesNoType.YES) {
          this.setInfusionDetailProcess(infusionDetail);
        }

        return [responseBody];
      }).catch((err) => {
        if (err.body) {
          const message = err.body.message;
          this.showToastErrorShowOption(message);
        }
      });
  }

  @OnMount({ target: InfusionsViewerAliases.INFUSION_REVERSE_LAST_EVENT_ACTION })
  handleMountContextualBarActionReverseLastEvent(): void {
    this.validateActionReverseLastEventIsVisible();
  }

  validateActionReverseLastEventIsVisible(): void {
    const { hourStatus: status, itemStatus } = this.historyService.getCurrentRouteParams();
    const allowAdministered                  = (this.state.param27 === YesNoType.YES) || (status === InfusionHourStatus.ADMINISTERED && itemStatus === InfusionItemStatus.RESTARTED);
    const allowPendingPreparation            = status === InfusionHourStatus.PENDING || status === InfusionHourStatus.PREPARATION;

    if(allowAdministered) {
      this.actionReverseLastEvent.setEnabled(false);
      return;
    }

    if(allowPendingPreparation) {
      this.actionReverseLastEvent.setEnabled(false);
    }
  }

  @OnUnmount({ target: InfusionsViewerAliases.LAYOUT_INFUSIONS_VIEWER })
  handleUnmountLayout(): void {
    InfusionsViewerParams.redirectToInfusionSchedule = false;
    InfusionsViewerParams.infusionScreenType = InfusionScreenType.ADMIN_BY_SCHEDULE;
  }
}

export default InfusionViewerController;
